import { z } from 'zod';
import { emailSchema } from './auth';
import { normalizedUuidSchema } from './common';

// ---------------------------------------------------------------------------
// 비회원 견적 요청(SUPER-83) 공용 원자 — 연락 방법·휴대폰 정규화·비회원 이메일·비회원 키.
// 견적 행·상세 계약(quotes.ts·quote-versions.ts)과 이미지 업로드 계약(design-asset.ts)이 이 파일을 쓰고, 요청·링크·연결 계약은
// guest-quotes.ts 에 있다. 이 파일은 다른 견적 계약을 import 하지 않는다 — guest-quotes.ts 가 quotes.ts 를 쓰므로 순환을 막는다.
// ---------------------------------------------------------------------------

/** 비회원 연락 방법(결정 G2) — DB enum `GuestContactMethod` 와 같은 값이다. 하나만 고른다. */
export const GUEST_CONTACT_METHODS = ['EMAIL', 'PHONE'] as const;
export type GuestContactMethod = (typeof GUEST_CONTACT_METHODS)[number];
export const guestContactMethodSchema = z.enum(GUEST_CONTACT_METHODS);

/** 휴대폰 칸 거부 문구 — 휴대폰이 아닌 번호(`02-…`·`070-…`)와 자릿수가 틀린 번호를 같은 문구로 돌려보낸다. */
export const GUEST_PHONE_INVALID_MESSAGE = '휴대폰 번호를 확인해 주세요';

/**
 * 사람이 휴대폰 번호에 흔히 넣는 문자 — 숫자·공백(스페이스만)·하이픈·점·괄호, 그리고 맨 앞의 국가번호(`+82` 또는 `(+82)`).
 * 글자·줄바꿈·탭이 섞이거나 `+` 가 가운데에 있으면 번호로 보지 않는다. 앞뒤 공백과 줄바꿈 없는 공백(NBSP, U+00A0)은 이 검사 전에 정리한다.
 */
const PHONE_INPUT_PATTERN = /^(?:\+|\(\+[0-9]{1,3}\))?[0-9\- ().]+$/;
/** 정규화한 한국 휴대폰 번호 — 010 + 8자리, 또는 011·016·017·018·019 + 7~8자리(옛 국번만 가운데 3자리가 있다). */
const KOREAN_MOBILE_PATTERN = /^(?:010\d{8}|01[16789]\d{7,8})$/;
const KOREA_COUNTRY_CODE = '82';

/**
 * 한국 휴대폰 번호를 숫자만의 국내 형식(`01012345678`)으로 바꾼다. 휴대폰이 아니면 null.
 *
 * 비회원이 남긴 번호와 본인인증 결과 번호를 같은 함수로 맞춰 비교한다(결정 G10) — `010-1234-5678`·`010 1234 5678`·
 * `+82 10-1234-5678`·`(+82) 10-1234-5678`·`821012345678` 은 모두 같은 값이 된다. 국가번호 뒤에 국내 접두 0 을 그대로 쓴 `+82 010-…` 도 받는다.
 */
export const normalizeKoreanMobilePhone = (value: string): string | null => {
  // 복사해 붙인 번호의 NBSP 를 스페이스로 맞추고 앞뒤 스페이스를 지운다 — 줄바꿈·탭은 그대로 둬 거부한다.
  const input = value.replace(/\u00A0/g, ' ').replace(/^ +| +$/g, '');
  if (!PHONE_INPUT_PATTERN.test(input)) return null;
  const digits = input.replace(/\D/g, '');
  const domestic = digits.startsWith(KOREA_COUNTRY_CODE)
    ? `0${digits.slice(KOREA_COUNTRY_CODE.length).replace(/^0/, '')}`
    : digits;
  return KOREAN_MOBILE_PATTERN.test(domestic) ? domestic : null;
};

/** 휴대폰 칸 — 입력 문자열을 정규화한 값으로 바꾼다. 휴대폰이 아니면 `GUEST_PHONE_INVALID_MESSAGE`. */
export const koreanMobilePhoneSchema = z
  .string()
  .max(30, GUEST_PHONE_INVALID_MESSAGE)
  .transform((value, ctx) => {
    const normalized = normalizeKoreanMobilePhone(value);
    if (normalized === null) {
      ctx.addIssue({ code: 'custom', message: GUEST_PHONE_INVALID_MESSAGE });
      return z.NEVER;
    }
    return normalized;
  });

/**
 * 비회원 이메일 — 앞뒤 공백을 지우고 소문자로 바꾼 뒤 회원 이메일(`emailSchema`)과 같은 규칙(형식·254자)으로 검사한다.
 * DB `guest_email` 은 소문자 평문이라(결정 G2) 연결 때 회원 이메일과 그대로 비교할 수 있다.
 */
export const guestEmailSchema = z.string().trim().toLowerCase().pipe(emailSchema);

/** 연락처 — 연락 방법에 따라 이메일 또는 휴대폰 하나만 싣는다. 다른 칸이 섞이면 strict 로 거부한다. */
export const guestContactSchema = z.discriminatedUnion('method', [
  z.strictObject({ method: z.literal('EMAIL'), email: guestEmailSchema }),
  z.strictObject({ method: z.literal('PHONE'), phone: koreanMobilePhoneSchema }),
]);
export type GuestContact = z.infer<typeof guestContactSchema>;

/**
 * 비회원 키(결정 G6) — 브라우저가 `crypto.randomUUID()` 로 만들어 `localStorage` 에 둔 값. 서버는 SHA-256 해시만 저장한다.
 * 대소문자가 달라 해시가 갈리지 않게 소문자로 맞춘다.
 */
export const guestKeySchema = normalizedUuidSchema;
